import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { NavLink, useNavigate, useLocation, matchPath } from "react-router-dom";
import { sendToFlutter } from "utils/flutterBridge";
import { createAbsoluteUrl } from "utils/constants";
import { redirectToAbsoluteUrl } from "utils/external-redirect";
import { useGlobalSlice } from "app/slice";
import { selectUser, selectIsAppView } from "app/slice/selectors";
import { useManageProfileSlice } from "app/pages/Customer/MyAccount/ManageProfile/slice";

import { Avatar, AvatarFallback, AvatarImage } from "app/components/ui/avatar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "app/components/ui/popover";

const myAccountItems = [
  { to: "/my-account/dashboard", label: "My Dashboard" },
  { to: "/my-account/payment-history", label: "Payment History" },
  { to: "/my-account/manage-profile", label: "Manage Profile" },
  { to: "/my-account/manage-cards", label: "Manage Cards" },
  { to: "/my-account/change-password", label: "Change Password" },
  { to: "/my-account/sim-service", label: "SIM Service" },
  // { to: "/support-tickets", label: "Support" },
  {
    to: createAbsoluteUrl("/support-tickets"),
    label: "Support",
    target: "_self",
  },
  { to: createAbsoluteUrl(), label: "Sign Out" },
];

export const MobileMyAccount = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [localProfileData, setLocalProfileData] = useState<any>(null);
  const user = useSelector(selectUser);
  const isAppView = useSelector(selectIsAppView);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { actions } = useGlobalSlice();
  const { useLazyGetProfileQuery } = useManageProfileSlice();
  const [
    getProfile,
    { data: profileData, isLoading: isProfileLoading, isError: isProfileError },
  ] = useLazyGetProfileQuery();

  useEffect(() => {
    if (user?._id) {
      getProfile({ customerId: user._id });
    } else {
      // Clear local profile data when user logs out
      setLocalProfileData(null);
    }
  }, [user?._id, getProfile]);

  useEffect(() => {
    // Update local profile data when API response changes
    if (profileData && user?._id) {
      setLocalProfileData(profileData);
    }
  }, [profileData, user?._id]);

  // Reset popover state when user authentication changes
  useEffect(() => {
    setIsOpen(false);
  }, [user?._id]);

  const handleAvatarClick = () => {
    if (!user?._id) {
      navigate("/login");
    } else {
      setIsOpen(!isOpen);
    }
  };

  const handleSignOut = () => {
    dispatch(
      actions.setUser({
        user: null,
        rememberDevice: false,
      }),
    );
    sendToFlutter("logout");
    setLocalProfileData(null); // Clear local profile data on sign out
    // navigate("/");
    redirectToAbsoluteUrl();
    setIsOpen(false);
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024 && isAppView !== "minimal") {
        setIsOpen(false);
      }
    };

    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Only show profile data if user is authenticated
  const shouldShowProfileData = user?._id && localProfileData;
  const profileImage = shouldShowProfileData
    ? localProfileData?.data?.image
    : null;
  const profileFirstName = shouldShowProfileData
    ? localProfileData?.data?.firstName
    : null;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Avatar
          className="lg:hidden items-center justify-center bg-[#BFBFBF1F] size-9 rounded-full cursor-pointer"
          onClick={handleAvatarClick}
          tabIndex={0}
          aria-label={
            user?._id ? "Open account menu" : "Sign in to your account"
          }
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleAvatarClick();
            }
          }}
        >
          <AvatarImage
            src={user ? profileImage : "/assets/profile.svg"}
            alt="Profile"
            className={profileImage ? "rounded-full size-full" : "size-5"}
          />
          <AvatarFallback className="bg-[#BFBFBF1F] uppercase text-black text-sm font-medium leading-3">
            {user?.firstName?.charAt(0) || profileFirstName?.charAt(0) || "U"}
          </AvatarFallback>
        </Avatar>
      </PopoverTrigger>
      {user?._id && (
        <PopoverContent className="overflow-hidden space-y-0.5 p-1 mr-5 sm:mr-6 md:mr-7 w-44">
          {(isAppView === "minimal"
            ? myAccountItems.filter(
                (item) =>
                  item.label === "My Dashboard" || item.label === "SIM Service",
              )
            : myAccountItems
          ).map((item, index) => (
            <React.Fragment key={index}>
              {item.label === "Sign Out" ? (
                <div
                  className="flex w-full text-sm leading-7 sm:leading-[1.8125rem] text-[#FE4D01] hover:text-[#E44501] font-semibold cursor-pointer hover:bg-[#FE4D0114] px-2 py-0.5 rounded"
                  onClick={handleSignOut}
                >
                  {item.label}
                </div>
              ) : item.target ? (
                <a
                  href={item.to}
                  target={item.target || "_blank"}
                  rel="noopener noreferrer"
                  className="relative flex w-full text-sm leading-7 sm:leading-[1.8125rem] hover:text-[#FE4D01] text-[#464646] font-medium hover:bg-[#FE4D0114] px-2 py-0.5 rounded"
                  onClick={() => setIsOpen(false)}
                >
                  {item.label}
                </a>
              ) : (
                <NavLink
                  to={item.to || ""}
                  onClick={() => setIsOpen(false)}
                  className={({ isActive }) =>
                    `relative flex w-full text-sm leading-7 sm:leading-[1.8125rem] hover:text-[#FE4D01] hover:bg-[#FE4D0114] px-2 py-0.5 rounded ${
                      isActive
                        ? "text-[#FE4D01] bg-[#FE4D0114] font-semibold"
                        : "text-[#464646] font-medium"
                    }`
                  }
                  tabIndex={0}
                  aria-label={item.label}
                >
                  {item.label}
                  {matchPath(
                    { path: item.to || "", end: true },
                    location.pathname,
                  ) && (
                    <img
                      src="/assets/tick-orange.svg"
                      alt="Tick"
                      className="shrink-0 size-4 absolute right-2 top-1/2 -translate-y-1/2"
                      aria-hidden="true"
                    />
                  )}
                </NavLink>
              )}
            </React.Fragment>
          ))}
        </PopoverContent>
      )}
    </Popover>
  );
};
